import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { EMPTY, forkJoin, Observable, tap } from 'rxjs';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { VatAiAcceptance, VatAiAcceptanceStatus } from 'src/app/common/VatAiAcceptance';
import { VatAiAcceptanceService } from 'src/app/services/vat-ai-acceptance.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

export interface VatAiAcceptanceRow {
  id: string;
  model: string;
  promptVersion: string;
  threshold: number;
  sampleSize: number;
  measuredPrecision: number;
  notes?: string | null;
  verifiedAt: Date;
  // True for the record the guard reads right now (current model + prompt version).
  isCurrent: boolean;
}

// Backend #579 (slice 3): the carrier-admin surface of the signed AI evaluation — the second
// sign-off guard for a Live VAT profile. Three parts: what runs right now and whether it is
// signed (status), the signing form (prompt version pinned to the running one — the backend
// refuses anything else), and the history of every record ever signed. The form takes
// percentages (a tax advisor's vocabulary); the wire carries fractions.
/** The acceptance sign-off form. Its type is inferred from here (#199). */
function buildSignForm(fb: FormBuilder) {
  return fb.group({
    promptVersion: [{ value: '', disabled: true }, [Validators.required]],
    // Backend: threshold in (0, 1], sample size ≥ 1, precision in [0, 1].
    thresholdPercent: [95, [Validators.required, Validators.min(0.01), Validators.max(100)]],
    // Empty to start with; a bare `null` would type the control as `null` (#199).
    sampleSize: fb.control<number>(null, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]),
    precisionPercent: fb.control<number>(null, [Validators.required, Validators.min(0), Validators.max(100)]),
    notes: fb.control<string>(null, [Validators.maxLength(1000)]),
  });
}
type SignForm = ReturnType<typeof buildSignForm>;

@Component({
  imports: [CommonModule, KENDO_GRID, ReactiveFormsModule, AsyncClickDirective, GridNoRecordsComponent],
  selector: 'app-vat-ai-acceptance',
  templateUrl: './vat-ai-acceptance.component.html',
})
export class VatAiAcceptanceComponent implements OnInit {
  private fb = inject(FormBuilder);
  private acceptanceService = inject(VatAiAcceptanceService);
  private apiActionService = inject(CallApiService);

  public status: VatAiAcceptanceStatus | null = null;
  public rows: VatAiAcceptanceRow[] = [];
  public signForm: SignForm;

  get f() {
    return this.signForm?.controls;
  }

  public ngOnInit() {
    this.signForm = this.createForm();
    this.load();
  }

  public reload() {
    this.load();
  }

  public submitForm(): Observable<unknown> {
    if (!this.signForm.valid) {
      this.signForm.markAllAsTouched();
      return EMPTY;
    }

    const value = this.signForm.getRawValue();
    return this.apiActionService
      .callWithToasts$(
        this.acceptanceService.create({
          promptVersion: value.promptVersion,
          threshold: VatAiAcceptanceComponent.toFraction(value.thresholdPercent),
          sampleSize: value.sampleSize,
          measuredPrecision: VatAiAcceptanceComponent.toFraction(value.precisionPercent),
          notes: value.notes?.trim() || null,
        }),
        $localize`Die KI-Auswertung wurde freigegeben.`,
        $localize`Beim Freigeben der KI-Auswertung ist ein Fehler aufgetreten.`
      )
      .pipe(
        tap(() => {
          this.resetForm();
          this.load();
        })
      );
  }

  // Percent → fraction, rounded to four places so 98.5 becomes 0.985 and not 0.98499….
  public static toFraction(percent: number): number {
    return Math.round(percent * 100) / 10000;
  }

  private createForm(): SignForm {
    return buildSignForm(this.fb);
  }

  private resetForm() {
    this.signForm.reset({
      promptVersion: this.status?.currentPromptVersion ?? '',
      thresholdPercent: 95,
      sampleSize: null,
      precisionPercent: null,
      notes: null,
    });
  }

  private load() {
    this.apiActionService.callWithToasts(
      forkJoin({
        status: this.acceptanceService.getStatus(),
        acceptances: this.acceptanceService.get(),
      }),
      null,
      $localize`Beim Laden der KI-Freigabe ist ein Fehler aufgetreten.`,
      ({ status, acceptances }) => {
        this.status = status;
        this.signForm.controls.promptVersion.setValue(status.currentPromptVersion ?? '');
        this.rows = acceptances
          .map((a) => this.toRow(a, status))
          .sort((a, b) => b.verifiedAt.getTime() - a.verifiedAt.getTime());
      }
    );
  }

  private toRow(acceptance: VatAiAcceptance, status: VatAiAcceptanceStatus): VatAiAcceptanceRow {
    return {
      id: acceptance.id,
      model: acceptance.model,
      promptVersion: acceptance.promptVersion,
      threshold: acceptance.threshold,
      sampleSize: acceptance.sampleSize,
      measuredPrecision: acceptance.measuredPrecision,
      notes: acceptance.notes,
      verifiedAt: new Date(acceptance.verifiedAt),
      isCurrent: !!status.acceptance && status.acceptance.id === acceptance.id,
    };
  }
}
